import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../theme/theme';

export interface EmptyStateWithLoaderProps {
    /** When true, only the loader is shown (no empty message). */
    loading: boolean;
    /** When true and not loading, the empty message is shown. */
    empty: boolean;
    /** Message to show when empty and not loading. Default: "No records available" */
    emptyMessage?: string;
    /** Minimum height of the container so loader and message align consistently. */
    minHeight?: number;
    /** Content to render when not loading and not empty. */
    children?: React.ReactNode;
}

/**
 * Renders either a loader, an empty state message, or children.
 * - While loading: ONLY a centered loader (no empty view, no "No Data").
 * - After loading, if empty: only a centered empty message (same alignment as loader).
 * - After loading, if not empty: children.
 *
 * Empty message is shown ONLY when loading is false and empty is true.
 * This avoids "No Data" flashing during tab switches or initial load.
 */
export const EmptyStateWithLoader: React.FC<EmptyStateWithLoaderProps> = ({
    loading,
    empty,
    emptyMessage = 'No records available',
    minHeight = 200,
    children,
}) => {
    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={minHeight}
                width="100%"
                data-testid="empty-state-loader"
            >
                <CircularProgress size={40} thickness={4} sx={{ color: colors.primaryLight[100] }} />
            </Box>
        );
    }

    if (empty) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={minHeight}
                width="100%"
                data-testid="empty-state-message"
            >
                <Stack alignItems="center" justifyContent="center" spacing={0}>
                    <Typography variant="body1" color="textSecondary" textAlign="center">
                        {emptyMessage}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return <>{children}</>;
};
